import {
  lazy,
  startTransition,
  Suspense,
  useContext,
  useEffect,
  useState,
} from "react";
import "react-datepicker/dist/react-datepicker.css";

import "./App.css";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { NoteState } from "./Context/NoteState";
import { toast, ToastContainer } from "react-toastify";
import Loader from "./Components/Includes/Loader/Loader";
import "react-loading-skeleton/dist/skeleton.css";
import "react-tooltip/dist/react-tooltip.css";
import axios from "axios";
import { Tooltip } from "react-tooltip";
import NotFound from "./NotFound";

const Home = lazy(() => import("./Components/Home/Home"));
const Login = lazy(() => import("./Components/Auth/Login"));
const Dashboard = lazy(() => import("./Components/Admin/Dashboard/Dashboard"));
const AdminLayout = lazy(() => import("./Components/Admin/AdminLayout"));
const ViewOrders = lazy(() => import("./Components/Admin/Orders/ViewOrders"));
const ViewOrderDetails = lazy(() =>
  import("./Components/Admin/Orders/ViewOrderDetails")
);

const AddCategories = lazy(() =>
  import("./Components/Admin/Categories/AddCategories")
);
const Accessories = lazy(() =>
  import("./Components/Admin/Categories/Accessories")
);
const AddProducts = lazy(() =>
  import("./Components/Admin/Products/AddProducts")
);
const ViewProducts = lazy(() =>
  import("./Components/Admin/Products/ViewProducts")
);
const ViewProductStock = lazy(() =>
  import("./Components/Admin/Products/ViewProductStock")
);
const ManageAdOn = lazy(() => import("./Components/Admin/Products/ManageAdOn"));

const ManageBranches = lazy(() =>
  import("./Components/Admin/Branches/ManageBranches")
);
const ManageUsers = lazy(() =>
  import("./Components/Admin/UsersManagement/ManageUsers")
);
const ProductDetail = lazy(() =>
  import("./Components/Admin/Products/ProductDetail")
);
const AddStaff = lazy(() => import("./Components/Admin/Staff/AddStaff"));
const Attendance = lazy(() => import("./Components/Admin/Staff/Attendance"));
const Salaries = lazy(() => import("./Components/Admin/Staff/Salaries"));
const Profile = lazy(() => import("./Profile"));

const Reports = lazy(() => import("./Components/Admin/Reports/Reports"));
const AccessDenied = lazy(() => import("./AccessDenied/AccessDenied"));
function App() {
  const navigate = useNavigate();
  const locate = useLocation();
  const { state, dispatch: ctxDispatch } = useContext(NoteState);
  const { accessToken, role } = state;
  useEffect(() => {
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        if (Math.floor(new Date().getTime() / 1000) > decoded.exp) {
          ctxDispatch({ type: "LOGOUT" });
          navigate("/");
        } else if (locate.pathname === "/") {
          navigate("/home");
        }

        const interceptor = axios.interceptors.response.use(
          (response) => response,
          async (error) => {
            if (error?.response?.status === 429) {
              window.location.href = "/error-429";
            } else if (
              error?.response?.status === 401 &&
              locate.pathname !== "/"
            ) {
              toast.error("Unauthorized!", {
                position: "top-right",
                autoClose: 1000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "colored",
              });

              setTimeout(async () => {
                await ctxDispatch({ type: "LOGOUT" });
                window.location.href = "/";
              }, 1000);
            }

            return Promise.reject(error);
          }
        );

        return () => {
          axios?.interceptors?.response?.eject(interceptor);
        };
      } catch (error) {
        navigate("/");
      }
    } else {
      navigate("/");
    }
  }, [accessToken, ctxDispatch, locate.pathname, navigate]);
  const [isToggled, setIsToggled] = useState(false);
  const handleToggle = () => {
    startTransition(() => {
      setIsToggled(!isToggled);
    });
  };
  return (
    <>
      <ToastContainer />
      <Tooltip id="actions-tooltip" />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/home" element={<Home />} />

          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<Dashboard />}>
              {" "}
            </Route>
            <Route path="add-products" element={<AddProducts />}>
              {" "}
            </Route>
            <Route path="view-products" element={<ViewProducts />}>
              {" "}
            </Route>
            <Route path="manage-categories" element={<AddCategories />}>
              {" "}
            </Route>
            <Route path="manage-accessories" element={<Accessories />}>
              {" "}
            </Route>
            <Route
              path="manage-branches"
              element={
                role === "superadmin" ? <ManageBranches /> : <AccessDenied />
              }
            />
            <Route
              path="manage-users"
              element={
                role === "superadmin" || role === "admin" ? (
                  <ManageUsers />
                ) : (
                  <AccessDenied />
                )
              }
            />
            <Route path="reports" element={<Reports />}>
              {" "}
            </Route>
            <Route path="orders" element={<ViewOrders />}>
              {" "}
            </Route>

            <Route path="order/:id" element={<ViewOrderDetails />}>
              {" "}
            </Route>

            <Route path="product-details" element={<ProductDetail />}>
              {" "}
            </Route>
            <Route path="stock-details/:id" element={<ViewProductStock />}>
              {" "}
            </Route>
            <Route path="manage-add-ons" element={<ManageAdOn />}>
              {" "}
            </Route>
            <Route
              path="add-staff"
              element={
                role === "superadmin" || role === "admin" ? (
                  <AddStaff />
                ) : (
                  <AccessDenied />
                )
              }
            />
            <Route
              path="staff-attendance"
              element={
                role === "superadmin" || role === "admin" ? (
                  <Attendance />
                ) : (
                  <AccessDenied />
                )
              }
            />
            <Route
              path="staff-salaries"
              element={
                role === "superadmin" || role === "admin" ? (
                  <Salaries />
                ) : (
                  <AccessDenied />
                )
              }
            />
          </Route>
          <Route path="*" element={<NotFound />}></Route>
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route
            path="/profile"
            element={
              <Profile isToggled={isToggled} handleToggle={handleToggle} />
            }
          />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
